import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import TwinTable from '../../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import ModalCreateTaskRepository from './ModalCreateTaskRepository'
import { useParams } from 'react-router-dom'
import { useAddDoubleTableGreenRowRender } from '../../../../../../baseComponents/DoubleTable/functions'
import { TaskRepositoryModelType } from '@teinor/erp/types/company/task/taskRepository'
import TwinIcon from '../../../../../../baseComponents/TwinIcon'
import { faUser } from '@fortawesome/pro-light-svg-icons'
import ModalEditTaskRepository from './ModalEditTaskRepository'
import ModalDeleteTaskRepository from './ModalDeleteTaskRepository'
import { CustomRenderRoundedImage, DeleteRow, EditRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderCustomer } from '../../../../../../specificComponents/Customers/Subcomponents'
import { RowData } from '../../../../../../baseComponents/TwinTable/types'
import { useCallback, useEffect, useState } from 'react'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import TwinToolTip from '../../../../../../baseComponents/TwinToolTip'

interface TaskRespositoriesProps extends ComponentWithPermissions { }

const TaskRespositories: React.FC<TaskRespositoriesProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('repository', 'Repositorio'),
        newText: t('newIssue', 'Nuevo tema'),
    }

    return (
        <BaseScreenTable TableComponent={TableTaskRespositories} CreateModalComponent={ModalCreateTaskRepository} EditModalComponent={ModalEditTaskRepository} DeleteModalComponent={ModalDeleteTaskRepository} pageData={pageData} userPermissions={userPermissions} />
    )
}

interface TableTaskRespositoriesProps extends TableComponentProps { }

const TableTaskRespositories: React.FC<TableTaskRespositoriesProps> = ({ setTableInstance, setOpened, userPermissions }) => {
    const { t } = useTwinTranslation()
    const { id } = useParams()
    const { taskRepositoryTags } = useTableTaskRepositoriesLogic()
    const { rowRenderer } = useAddDoubleTableGreenRowRender(id || '')
    const columns: ColumnTableSchema<TaskRepositoryModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            sortable: true,
            width: 80
        },
        {
            id: 'title',
            dataKey: 'title',
            label: t('title', 'Título'),
            minWidth: 140,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('title', 'Título') }
            }
        },
        {
            id: 'customer',
            dataKey: 'CustomerId',
            label:t('customer', 'Cliente'),
            customRender: (_parameterValue, allRowData) => <CustomRenderCustomer allRowData={allRowData} />,
            minWidth: 120,
            searchKey: 'Customer.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerName', 'Nombre cliente') }
            },
        },
        {
            id:'TaskRepositoryTags',
            dataKey: 'TaskRepositoryTags',
            label: t('tags', 'Etiquetas'),
            customRender: (_parameterValue, allRowData) => <RenderTaskRepositoryTags {...{ allRowData, taskRepositoryTags }} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: taskRepositoryTags || {}, label: t('tags', 'Etiquetas') }
            },
            generalSearchable: false,
            sortable: false,
        },
        {
            id:'TaskRepositoryAnswers',
            dataKey: 'TaskRepositoryAnswers',
            label: t('comments', 'Comentarios'),
            customRender: (_parameterValue, allRowData) => <div>{allRowData.answerCount}</div>,
        },
        {
            id: 'EmployeeId',
            dataKey: 'EmployeeId',
            simpleLabel: t('employee', 'Empleado'),
            label: () => <TwinIcon icon={faUser} className='w-16 h-16 mx-auto' />,
            customRender: (_parameterValue, allRowData) => <CustomRenderRoundedImage className='cursor-pointer h-34 w-34' icon={faUser} img={allRowData?.Employee?.profile_image || ''} name={allRowData?.Employee?.fullname_short} />,
            width: 50
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]

    return (
        <TwinTable name='TaskRepositories' columns={columns} getDataFrom='/api/app/task/repository/getAllTaskRepositories' setTableInstance={setTableInstance} rowRenderer={rowRenderer} hideLoadFiltersButton={true} onRowClick={(_id, rowData) => setOpened({
            type: 'edit',
            allRowData: rowData
        })} />
    )
}

const useTableTaskRepositoriesLogic = () => {
    const [taskRepositoryTags, setTaskRepositoryTags] = useState<TwinDictionary | null>(null)
    
    const getTaskRepositoryTags = useCallback(async() => {
        const result = await twinFetchPostJSON('/api/app/task/repository/tag/getAllTaskRepositoryTags', {})
        if(result){
            setTaskRepositoryTags(dictionaryComplexFromJsonArr(result))
        }
    }, [setTaskRepositoryTags])

    useEffect(() => {
        getTaskRepositoryTags()
    }, [getTaskRepositoryTags])
    
    return { taskRepositoryTags }
}


interface RenderTaskRepositoryTagsProps {
    allRowData: RowData
    taskRepositoryTags: TwinDictionary | null
}

const RenderTaskRepositoryTags: React.FC<RenderTaskRepositoryTagsProps> = ({ allRowData, taskRepositoryTags }) => {
    if(!allRowData?.TaskRepositoryTagRels.length){
        return <div>--</div>
    }
    const renderThis: JSX.Element[] = []
    const shortVersion = allRowData.TaskRepositoryTagRels.length > 4
    for (const key in allRowData.TaskRepositoryTagRels) {
        const tagId = allRowData.TaskRepositoryTagRels[key].TaskRepositoryTagId
        const tag = taskRepositoryTags?.[tagId]
        const id = 'TaskRepository_' + allRowData?.id + '_' + tagId
        renderThis.push(
        <div>
            {shortVersion ?<TwinToolTip id={id} place='top' content={tag?.name} />: null}
            <div className='flex items-center ' data-tooltip-id={id}>
                <div className={'rounded-full h-10 w-10 border border-gray-D6 '} style={{ backgroundColor: tag?.color }} />
                {!shortVersion ?<span className='ml-5'>{tag?.name}</span>: null}
            </div>
        </div>)
    }
    return (
        <div className={'flex items-center flex-wrap flex-auto ' + (shortVersion? 'gap-x-5' : 'gap-x-10')}>
            {renderThis}
        </div>
    )
}



export default TaskRespositories